/**
 * 3.0 콜랩의 날짜 · 시각 · 금액 조판 — 순수 함수.
 * @description 날짜 표기는 예전 2.0 웹 상세와 같은 `en-GB`(`16 October` ·
 *   `13:45`)다. 날짜 줄 규칙은 앱 `formatDetailSchedule` 과 같다 — 같은 날이면 시간대, 여러 날이면
 *   기간, 끝이 없으면 시작 날짜와 시각, 시작이 없으면 상시. 2.0 콜랩은 날짜만 쓴다.
 *
 *   시간대는 부르는 쪽이 정해 넘긴다(`CollabV3TimeZone`). 같은 시간대 안에서만 날짜를 견준다.
 */
const LOCALE = "en-GB";
const NUMBER_FORMAT = new Intl.NumberFormat("en");
const formatterCache = new Map<string, Intl.DateTimeFormat>();

const FORMATS = {
  day: { day: "numeric", month: "long" },
  short: { day: "numeric", month: "short" },
  clock: { hour: "2-digit", minute: "2-digit", hourCycle: "h23" },
  key: { year: "numeric", month: "2-digit", day: "2-digit" },
} satisfies Record<string, Intl.DateTimeFormatOptions>;

type FormatKind = keyof typeof FORMATS;

function format(kind: FormatKind, iso: string, timeZone: string): string {
  const cacheKey = `${kind}|${timeZone}`;
  let formatter = formatterCache.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(LOCALE, { ...FORMATS[kind], timeZone });
    formatterCache.set(cacheKey, formatter);
  }
  return formatter.format(new Date(iso));
}

/** `3,200` — 금액 · 인원 */
export const formatCount = (value: number): string =>
  NUMBER_FORMAT.format(value);

/** `16 October` */
export const formatDay = (iso: string, timeZone: string): string =>
  format("day", iso, timeZone);

/** `16 Oct` — 스레드 글처럼 좁은 자리 */
export const formatShortDay = (iso: string, timeZone: string): string =>
  format("short", iso, timeZone);

/** `13:45` */
export const formatClock = (iso: string, timeZone: string): string =>
  format("clock", iso, timeZone);

/** `16 October 13:45` */
export const formatDayTime = (iso: string, timeZone: string): string =>
  `${formatDay(iso, timeZone)} ${formatClock(iso, timeZone)}`;

const isSameDay = (a: string, b: string, timeZone: string): boolean =>
  format("key", a, timeZone) === format("key", b, timeZone);

/** 날짜 줄. `title` 이 null 이면 상시 콜랩, `isPeriod` 면 아랫줄을 "기간 안에 언제든" 으로 채운다 */
export interface CollabV3ScheduleText {
  title: string | null;
  subtitle: string | null;
  isPeriod: boolean;
}

const EMPTY_SCHEDULE: CollabV3ScheduleText = {
  title: null,
  subtitle: null,
  isPeriod: false,
};

/**
 * 2.0 콜랩 — 날짜만. 시작이 없으면 끝 날짜 하나, 같은 날이면 하루, 아니면 기간.
 * @description 2.0 의 시각은 사람이 고른 값이 아니라(마감만 있는 콜랩의 `09:45:05` 등) 찍지 않는다.
 *   둘 다 없으면 `title` 이 null 이고 화면은 줄을 숨긴다.
 */
function formatLegacySchedule(
  startAt: string | null,
  endAt: string | null,
  timeZone: string,
): CollabV3ScheduleText {
  const first = startAt ?? endAt;
  if (!first) return EMPTY_SCHEDULE;
  const title =
    startAt && endAt && !isSameDay(startAt, endAt, timeZone)
      ? `${formatDay(startAt, timeZone)} – ${formatDay(endAt, timeZone)}`
      : formatDay(endAt ?? first, timeZone);
  return { title, subtitle: null, isPeriod: false };
}

export function formatSchedule(
  startAt: string | null,
  endAt: string | null,
  timeZone: string,
  isLegacy = false,
): CollabV3ScheduleText {
  if (isLegacy) return formatLegacySchedule(startAt, endAt, timeZone);
  if (!startAt) return EMPTY_SCHEDULE;
  const day = formatDay(startAt, timeZone);
  const clock = formatClock(startAt, timeZone);
  if (!endAt) return { title: day, subtitle: clock, isPeriod: false };
  if (isSameDay(startAt, endAt, timeZone)) {
    const until = formatClock(endAt, timeZone);
    return { title: day, subtitle: `${clock} – ${until}`, isPeriod: false };
  }
  return {
    title: `${day} – ${formatDay(endAt, timeZone)}`,
    subtitle: null,
    isPeriod: true,
  };
}
